import posthog from "posthog-js";
import type {
  BeforeSendFn,
  CaptureResult,
  Properties,
  SeverityLevel,
} from "posthog-js";

type ExceptionMechanism = {
  handled?: boolean;
  type?: string;
};

type ExceptionFrame = {
  abs_path?: string;
  colno?: number;
  filename?: string;
  function?: string;
  in_app?: boolean;
  lineno?: number;
  module?: string;
};

type ExceptionItem = {
  mechanism?: ExceptionMechanism;
  stacktrace?: {
    frames?: ExceptionFrame[];
  };
  type?: string;
  value?: string;
};

type ExceptionClassification = {
  name: string;
  reason?: string;
  severity: SeverityLevel;
  suppress?: boolean;
};

const BUILD_VERSION_META_SELECTOR = 'meta[name="build-version"]';
const METADATA_SCHEMA = "client_exception_metadata_v1";
const ABORT_ERROR_PATTERN =
  /\bAbortError\b|operation (?:was )?aborted|signal is aborted|abort(?:ed)? without reason/i;
const TRANSIENT_FETCH_PATTERN =
  /Failed to fetch|Load failed|NetworkError when attempting to fetch resource|cancelled|canceled/i;
const REACT_RECOVERABLE_ERROR_PATTERN =
  /Minified React error #520|invariant=520|error during concurrent rendering.*React was able to recover/i;
const LAZY_CHUNK_LOAD_PATTERN =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|ChunkLoadError|Loading chunk \d+ failed|Importing a module script failed|Unable to preload CSS/i;
// React.lazy surfaces a failed/empty dynamic import as a TypeError thrown while
// reading the resolved module's `.default` export, rather than the standard
// "Failed to fetch dynamically imported module" message above. Observed in prod
// across browsers as:
//   Chrome:  Cannot read properties of undefined (reading 'default')
//   Firefox: can't access property "default", e._result is undefined
//   Safari:  undefined is not an object (evaluating 'e._result.default')
// These are transient chunk-load failures (spread across many routes, ~1 user
// each), so treat them as recoverable: the ErrorBoundary reloads once (fetching
// fresh chunks) instead of showing a fatal crash screen.
const LAZY_MODULE_RESOLUTION_PATTERN =
  /_result is undefined|evaluating ['"][^'"]*_result\.default|reading ['"]default['"]\)|access property ['"]default['"]/i;
const PROVIDER_CONTEXT_RECOVERY_PATTERN =
  /Unable to find tRPC Context|DialogPortal must be used within Dialog|Cannot read properties of null \(reading ['"]useContext['"]\)/i;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const asString = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value : undefined;

const severityLevels = new Set<SeverityLevel>([
  "debug",
  "error",
  "fatal",
  "info",
  "log",
  "warning",
]);

const asSeverityLevel = (value: unknown): SeverityLevel | undefined =>
  typeof value === "string" && severityLevels.has(value as SeverityLevel)
    ? (value as SeverityLevel)
    : undefined;

const truncate = (
  value: string | undefined,
  maxLength: number,
): string | undefined => {
  if (!value) return undefined;
  return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;
};

const collectErrorText = (
  value: unknown,
  parts: string[],
  seen: WeakSet<object>,
  depth = 0,
) => {
  if (value === undefined || value === null || depth > 3) return;

  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    parts.push(String(value));
    return;
  }

  if (value instanceof Error) {
    parts.push(value.name, value.message);
    collectErrorText(value.cause, parts, seen, depth + 1);
    return;
  }

  if (typeof value !== "object") return;
  if (seen.has(value)) return;
  seen.add(value);

  const record = value as Record<string, unknown>;
  for (const key of [
    "name",
    "type",
    "message",
    "value",
    "reason",
    "detail",
    "cause",
  ]) {
    if (key in record) {
      collectErrorText(record[key], parts, seen, depth + 1);
    }
  }
};

export const getClientExceptionText = (value: unknown): string => {
  const parts: string[] = [];
  collectErrorText(value, parts, new WeakSet());
  return parts.filter(Boolean).join(" ");
};

export const isAbortLikeError = (error: unknown): boolean =>
  ABORT_ERROR_PATTERN.test(getClientExceptionText(error));

export const isReactRecoverableError = (error: unknown): boolean =>
  REACT_RECOVERABLE_ERROR_PATTERN.test(getClientExceptionText(error));

export const isLazyChunkLoadError = (error: unknown): boolean => {
  const text = getClientExceptionText(error);
  return (
    LAZY_CHUNK_LOAD_PATTERN.test(text) ||
    LAZY_MODULE_RESOLUTION_PATTERN.test(text)
  );
};

export const isProviderContextRecoveryError = (error: unknown): boolean =>
  PROVIDER_CONTEXT_RECOVERY_PATTERN.test(getClientExceptionText(error));

export const isPageOpenRecoveryError = (error: unknown): boolean =>
  isLazyChunkLoadError(error) || isProviderContextRecoveryError(error);

const readBuildVersion = (): string => {
  if (typeof document === "undefined") return "server";
  return (
    document
      .querySelector(BUILD_VERSION_META_SELECTOR)
      ?.getAttribute("content") ?? "dev"
  );
};

const readRouteContext = () => {
  if (typeof window === "undefined") {
    return {
      hashPresent: false,
      origin: undefined,
      path: undefined,
      searchKeys: [] as string[],
    };
  }

  return {
    hashPresent: Boolean(window.location.hash),
    origin: window.location.origin,
    path: window.location.pathname,
    searchKeys: Array.from(
      new URLSearchParams(window.location.search).keys(),
    ).sort(),
  };
};

const readReferrerOrigin = (): string | undefined => {
  if (typeof document === "undefined" || !document.referrer) return undefined;

  try {
    return new URL(document.referrer).origin;
  } catch {
    return undefined;
  }
};

const readClientContext = () => {
  const route = readRouteContext();

  return {
    ...route,
    buildVersion: readBuildVersion(),
    language: typeof navigator === "undefined" ? undefined : navigator.language,
    online: typeof navigator === "undefined" ? undefined : navigator.onLine,
    referrerOrigin: readReferrerOrigin(),
    userAgent:
      typeof navigator === "undefined" ? undefined : navigator.userAgent,
    viewport:
      typeof window === "undefined"
        ? undefined
        : `${window.innerWidth}x${window.innerHeight}`,
    visibilityState:
      typeof document === "undefined" ? undefined : document.visibilityState,
  };
};

const toExceptionList = (
  properties: Properties | undefined,
): ExceptionItem[] => {
  const exceptionList = properties?.$exception_list;
  if (!Array.isArray(exceptionList)) return [];

  return exceptionList.filter(isRecord).map((item) => {
    const mechanism = isRecord(item.mechanism) ? item.mechanism : undefined;
    const stacktrace = isRecord(item.stacktrace) ? item.stacktrace : undefined;
    const frames = Array.isArray(stacktrace?.frames)
      ? stacktrace.frames.filter(isRecord).map((frame) => ({
          abs_path: asString(frame.abs_path),
          colno: typeof frame.colno === "number" ? frame.colno : undefined,
          filename: asString(frame.filename),
          function: asString(frame.function),
          in_app: typeof frame.in_app === "boolean" ? frame.in_app : undefined,
          lineno: typeof frame.lineno === "number" ? frame.lineno : undefined,
          module: asString(frame.module),
        }))
      : undefined;

    return {
      mechanism: mechanism
        ? {
            handled:
              typeof mechanism.handled === "boolean"
                ? mechanism.handled
                : undefined,
            type: asString(mechanism.type),
          }
        : undefined,
      stacktrace: frames ? { frames } : undefined,
      type: asString(item.type),
      value: asString(item.value),
    };
  });
};

const getExceptionText = (
  exceptionList: ExceptionItem[],
  properties?: Properties,
): string =>
  [
    ...exceptionList
      .flatMap((exception) => [
        exception.type,
        exception.value,
        exception.mechanism?.type,
      ])
      .filter(Boolean),
    asString(properties?.$exception_message),
    asString(properties?.$exception_type),
    asString(properties?.message),
    asString(properties?.name),
  ]
    .filter(Boolean)
    .join(" ");

const classifyException = (
  exceptionList: ExceptionItem[],
  properties?: Properties,
): ExceptionClassification => {
  const first = exceptionList[0];
  const mechanism = first?.mechanism?.type ?? "";
  const text = getExceptionText(exceptionList, properties);
  const isUnhandledPromise =
    mechanism === "onunhandledrejection" || /promise|rejection/i.test(text);

  if (/ResizeObserver loop/i.test(text)) {
    return {
      name: "browser_resize_observer",
      reason: "Browser ResizeObserver delivery noise.",
      severity: "info",
      suppress: true,
    };
  }

  if (ABORT_ERROR_PATTERN.test(text)) {
    return {
      name: "browser_abort",
      reason:
        "Request or media work was aborted by navigation or state change.",
      severity: "info",
      suppress: true,
    };
  }

  if (
    /\bNotSupportedError\b|play\(\) request was interrupted|not allowed to play|user didn't interact|no supported source/i.test(
      text,
    )
  ) {
    return {
      name: "media_playback_transient",
      reason:
        "Browser media playback rejected a non-critical play/load request.",
      severity: "info",
      suppress: true,
    };
  }

  if (
    LAZY_CHUNK_LOAD_PATTERN.test(text) ||
    LAZY_MODULE_RESOLUTION_PATTERN.test(text)
  ) {
    return {
      name: "chunk_load_recovered",
      reason: "Lazy chunk failed, handled by build-version reload recovery.",
      severity: "warning",
    };
  }

  if (
    TRANSIENT_FETCH_PATTERN.test(text) &&
    (isUnhandledPromise || first?.type === "TypeError")
  ) {
    return {
      name: "network_fetch_transient",
      reason: "Browser fetch failed outside a user-visible app crash path.",
      severity: "info",
      suppress: true,
    };
  }

  if (REACT_RECOVERABLE_ERROR_PATTERN.test(text)) {
    return {
      name: "react_concurrent_recovery",
      reason: "React concurrent recovery surfaced a minified invariant.",
      severity: "warning",
      suppress: true,
    };
  }

  if (PROVIDER_CONTEXT_RECOVERY_PATTERN.test(text)) {
    return {
      name: "react_provider_context_missing",
      reason:
        "React provider context was missing during render or deploy recovery.",
      severity: "warning",
    };
  }

  return {
    name: "unclassified_client_exception",
    severity: "error",
  };
};

const getFrames = (exceptionList: ExceptionItem[]): ExceptionFrame[] =>
  exceptionList.flatMap((exception) => exception.stacktrace?.frames ?? []);

const isAppFrame = (frame: ExceptionFrame): boolean => {
  const filename = frame.filename ?? frame.abs_path ?? "";
  return (
    filename.includes("/assets/") ||
    filename.includes("/src/") ||
    filename.includes("aisongmaker") ||
    frame.in_app === true
  );
};

const serializeFrame = (frame: ExceptionFrame | undefined) => {
  if (!frame) return undefined;

  return {
    colno: frame.colno,
    filename: truncate(frame.filename ?? frame.abs_path, 240),
    function: truncate(frame.function, 160),
    inApp: frame.in_app,
    lineno: frame.lineno,
    module: truncate(frame.module, 160),
  };
};

const buildExceptionMetadata = (
  exceptionList: ExceptionItem[],
  classification: ExceptionClassification,
  source = "web_client",
) => {
  const first = exceptionList[0];
  const frames = getFrames(exceptionList);
  const appFrame = [...frames].reverse().find(isAppFrame);
  const topFrame = frames[frames.length - 1] ?? frames[0];

  return {
    ...readClientContext(),
    appFrame: serializeFrame(appFrame),
    classification: classification.name,
    exceptionMessage: truncate(first?.value, 500),
    exceptionType: first?.type,
    frameCount: frames.length,
    handled: first?.mechanism?.handled,
    mechanism: first?.mechanism?.type,
    reason: classification.reason,
    schema: METADATA_SCHEMA,
    severity: classification.severity,
    source,
    topFrame: serializeFrame(topFrame),
  };
};

const mergeMetadata = (
  existing: unknown,
  metadata: ReturnType<typeof buildExceptionMetadata>,
) => ({
  ...(isRecord(existing) ? existing : {}),
  ...metadata,
});

export const posthogExceptionBeforeSend: BeforeSendFn = (
  captureResult: CaptureResult | null,
): CaptureResult | null => {
  if (!captureResult || captureResult.event !== "$exception") {
    return captureResult;
  }

  const properties = captureResult.properties ?? {};
  const exceptionList = toExceptionList(properties);
  const classification = classifyException(exceptionList, properties);

  if (classification.suppress) {
    // Network fetch-transients are normally noise, but a SURGE of them is the
    // user-visible symptom of a backend/DB outage — which we must still be able
    // to detect. Keep a small sample (tagged) instead of dropping them all;
    // all other suppressed classes stay fully dropped.
    if (
      classification.name === "network_fetch_transient" &&
      Math.random() < 0.05
    ) {
      properties.network_transient_sampled = true;
    } else {
      return null;
    }
  }

  const effectiveClassification: ExceptionClassification = {
    ...classification,
    name:
      typeof properties.error_classification === "string"
        ? properties.error_classification
        : classification.name,
    severity:
      asSeverityLevel(properties.$exception_level) ?? classification.severity,
  };
  const errorSource =
    typeof properties.error_source === "string"
      ? properties.error_source
      : "web_client";
  const metadata = buildExceptionMetadata(
    exceptionList,
    effectiveClassification,
    errorSource,
  );

  return {
    ...captureResult,
    properties: {
      ...properties,
      $exception_level:
        asSeverityLevel(properties.$exception_level) ??
        effectiveClassification.severity,
      app_exception_metadata: metadata,
      error_classification: effectiveClassification.name,
      error_source: errorSource,
      metadata: mergeMetadata(properties.metadata, metadata),
    },
  };
};

export function captureReactErrorBoundaryException(
  error: Error,
  componentStack?: string | null,
) {
  try {
    if (isAbortLikeError(error) || isReactRecoverableError(error)) return;

    const metadata = {
      ...readClientContext(),
      classification: "react_error_boundary",
      componentStack: truncate(componentStack ?? undefined, 4000),
      exceptionMessage: truncate(error.message, 500),
      exceptionType: error.name,
      schema: METADATA_SCHEMA,
      severity: "fatal" satisfies SeverityLevel,
      source: "react_error_boundary",
    };

    posthog.captureException(error, {
      $exception_level: "fatal",
      app_exception_metadata: metadata,
      component_stack: metadata.componentStack,
      error_classification: "react_error_boundary",
      error_source: "react_error_boundary",
      metadata,
    });
  } catch {
    // Exception reporting must never throw from the app error boundary.
  }
}
